import { useEffect, useRef, useState, type CSSProperties, type PointerEvent, type RefObject } from 'react'
import clsx from 'clsx'

import { buzz, MOBILE_GOO, pillMode, useReducedMotion } from './motion'
import { TABS, tabIndex, type TabKey } from './nav'
import { IDLE_VIEW, MENU_ITEMS, TabGesture, type GestureView, type MenuItem } from './tabGesture'
import { MENU_ICON_PATHS, MenuGlyph, PlusGlyph, TabIcon } from './tabIcons'

// ============================================================================
// TAB BAR B — docs/design/handoff-mobile-v10, boards 03 (TabScrubB → TabScrub, behaviour B),
// 04 (Bar) and 27 (TabIcons). Owner decision 2026-10-18: bar B, five tabs, + on the right.
//
// A FLOATING DARK CAPSULE: 70px tall, 14px from each side, 20px above the bottom safe area,
// radius 35. Five 56px tabs in the region left of the 56px orange +. The active tab sits on a
// pill; under the goo filter (MOBILE_GOO) it is two pills, a quick lead and a springy trail, that
// melt into one blob while they travel.
//
// THE GESTURE IS mobile/shell/tabGesture.ts; this file only measures, renders and wires. Pointer
// moves go straight into the gesture object, which calls `render` only when something visible
// changed (a tab crossed, the menu opened, an item lit), so a drag costs a React render per
// crossing, never per move.
//
// KEYBOARD STILL WORKS. Tabs are links and the + and the menu items are buttons; a click with
// `detail === 0` is a keyboard activation and is handled as one. A pointer's click (detail ≥ 1)
// is ignored because the gesture already acted on the pointer itself.
// ============================================================================

const MENU_LABEL: Record<MenuItem, string> = {
  voice: 'Voice memo',
  reminder: 'Reminder',
  note: 'Note',
  ask: 'Ask',
}

export function TabBar({
  active,
  away,
  tasksNeedAttention,
  schoolNeedsAttention,
  onSelect,
  onPick,
  wrapRef,
}: {
  active: TabKey
  /**
   * Why the bar is out of view, if it is: a pushed screen (board 25 — it slides away with the
   * push), or the soft keyboard (owner, 2026-10-19 — the bar stays locked to the bottom of the
   * screen, so it steps down behind the keyboard instead of riding up on top of it).
   */
  away: 'push' | 'keyboard' | null
  /** Anything open and past its date — the dot Reminders carries. */
  tasksNeedAttention: boolean
  /** A connector in error — the dot School carries. */
  schoolNeedsAttention: boolean
  /** A tab was chosen. It may be the active one: that is a re-tap. */
  onSelect: (tab: TabKey) => void
  /** A capture menu item was picked. */
  onPick: (item: MenuItem) => void
  /** The bar's outer element, which the edge-swipe moves in step with the finger. */
  wrapRef: RefObject<HTMLDivElement | null>
}) {
  const reduced = useReducedMotion()
  const pills = pillMode(MOBILE_GOO, reduced)
  const [view, setView] = useState<GestureView>(IDLE_VIEW)
  // The tab just chosen, held until the router catches up. The gesture clears `hover` on release
  // (an urgent render) while the navigation lands a transition later; without this the pill is
  // drawn on the OLD tab in between and rubber-bands back to the new one.
  const [pending, setPending] = useState<number | null>(null)

  const navRef = useRef<HTMLElement>(null)
  const capRef = useRef<HTMLButtonElement>(null)
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const latest = useRef({ onSelect, onPick })
  latest.current = { onSelect, onPick }
  const activeIndex = tabIndex(active)
  const activeRef = useRef(activeIndex)
  activeRef.current = activeIndex

  /** Every way of choosing a tab (pointer release, keyboard) goes through here. */
  const choose = useRef((i: number) => {
    if (i !== activeRef.current) setPending(i)
    latest.current.onSelect(TABS[i]!.key)
  }).current

  const gestureRef = useRef<TabGesture | null>(null)
  if (gestureRef.current === null) {
    gestureRef.current = new TabGesture({
      measure: () => {
        const nav = navRef.current
        const cap = capRef.current
        if (!nav || !cap) return null
        const bar = nav.getBoundingClientRect()
        const c = cap.getBoundingClientRect()
        // The capsule is never rotated or scaled, so its rect over its layout width is the
        // app's zoom. (The + is rotated while the menu is open; only its CENTRE is used.)
        const scale = nav.offsetWidth > 0 && bar.width > 0 ? bar.width / nav.offsetWidth : 1
        const tabs = tabRefs.current.map((el) => {
          const r = el?.getBoundingClientRect()
          return r
            ? { left: r.left, right: r.right, top: r.top, bottom: r.bottom }
            : { left: 0, right: 0, top: 0, bottom: 0 }
        })
        return {
          tabs,
          bar: { left: bar.left, right: bar.right, top: bar.top, bottom: bar.bottom },
          cap: { x: c.left + c.width / 2, y: c.top + c.height / 2 },
          scale,
        }
      },
      render: setView,
      select: choose,
      pick: (item) => latest.current.onPick(item),
      buzz: () => buzz(),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (handle) => window.clearTimeout(handle as number),
    })
  }
  const gesture = gestureRef.current

  useEffect(() => () => gesture.dispose(), [gesture])

  const hidden = away !== null

  // A hidden bar cannot hold an open menu, and (being inert) cannot open one either.
  useEffect(() => {
    if (hidden) gesture.closeMenu()
  }, [hidden, gesture])

  // Escape closes the menu, for the keyboard that opened it.
  useEffect(() => {
    if (!view.menu) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') gesture.closeMenu()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view.menu, gesture])

  // The active tab changing at all means the navigation landed (on `pending`) or went elsewhere:
  // either way the router is the truth again.
  useEffect(() => {
    setPending(null)
  }, [activeIndex])

  const shown = view.hover >= 0 ? view.hover : (pending ?? activeIndex)
  const pillStyle: CSSProperties = { transform: `translateX(${shown * 100}%)` }

  function point(e: PointerEvent): { x: number; y: number } | null {
    return Number.isFinite(e.clientX) && Number.isFinite(e.clientY) ? { x: e.clientX, y: e.clientY } : null
  }

  return (
    <div
      ref={wrapRef}
      className="m-bar-wrap"
      data-hidden={hidden ? 'true' : 'false'}
      data-away={away ?? undefined}
      data-reduced={reduced ? 'true' : undefined}
      aria-hidden={hidden || undefined}
      inert={hidden}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        if (hidden || e.button > 0) return
        const p = point(e)
        if (!p) return
        if (gesture.down(p)) {
          try {
            e.currentTarget.setPointerCapture(e.pointerId)
          } catch {
            // A synthetic or already-released pointer cannot be captured; moves still bubble here.
          }
        }
      }}
      onPointerMove={(e) => {
        const p = point(e)
        if (p) gesture.move(p)
      }}
      onPointerUp={(e) => {
        const p = point(e)
        if (p) gesture.up(p)
        else gesture.cancel()
      }}
      onPointerCancel={() => gesture.cancel()}
    >
      <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
        <defs>
          <filter id="m-goo" x="-30%" y="-60%" width="160%" height="220%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -9" />
          </filter>
        </defs>
      </svg>

      <nav ref={navRef} aria-label="Sections" className="m-bar bg-m-bar">
        <div className="m-pills" data-goo={pills === 'goo' ? 'true' : undefined} aria-hidden="true">
          {pills === 'goo' && <span className="m-pill m-pill-trail bg-m-bar-pill" style={pillStyle} />}
          <span
            className={clsx(
              'm-pill bg-m-bar-pill',
              pills === 'goo' ? 'm-pill-lead' : pills === 'solo' ? 'm-pill-solo' : 'm-pill-still',
            )}
            style={pillStyle}
          />
        </div>

        <div className="m-tabs">
          {TABS.map((tab, i) => {
            const isActive = i === activeIndex
            const isShown = i === shown
            const attention =
              (tab.key === 'reminders' && tasksNeedAttention) || (tab.key === 'school' && schoolNeedsAttention)
            return (
              <a
                key={tab.key}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                href={tab.to}
                draggable={false}
                aria-current={isActive ? 'page' : undefined}
                className={clsx('m-tap m-tab', isShown ? 'text-m-tab-fg-active' : 'text-m-tab-fg')}
                onClick={(e) => {
                  e.preventDefault()
                  if (e.detail === 0) choose(i)
                }}
              >
                <span className="relative">
                  <TabIcon tab={tab.key} play={isActive} accent={isShown} lifted={view.hover === i} />
                  {attention && <span className="m-tab-dot bg-m-accent" aria-hidden="true" />}
                </span>
                <span className="m-tab-label">{tab.label}</span>
                {attention && <span className="sr-only">— something needs attention</span>}
              </a>
            )
          })}
        </div>
      </nav>

      <div className="m-scrim" data-open={view.menu ? 'true' : 'false'} aria-hidden="true" />

      <div className="m-cap-anchor">
        <div role="menu" aria-label="Capture" className="m-menu" inert={!view.menu}>
          {MENU_ITEMS.map((item, i) => (
            <button
              key={item}
              type="button"
              role="menuitem"
              aria-label={MENU_LABEL[item]}
              className="m-menu-item"
              data-open={view.menu ? 'true' : 'false'}
              data-hot={view.item === i ? 'true' : 'false'}
              style={{ '--m-i': i } as CSSProperties}
              onClick={(e) => {
                if (e.detail !== 0 || hidden) return
                gesture.closeMenu()
                latest.current.onPick(item)
              }}
            >
              <span className="m-menu-body">
                <span className="m-menu-disc">
                  <MenuGlyph d={MENU_ICON_PATHS[item]} />
                </span>
                <span className="m-menu-label">{MENU_LABEL[item]}</span>
              </span>
            </button>
          ))}
        </div>

        <button
          ref={capRef}
          type="button"
          aria-label={view.menu ? 'Close capture menu' : 'Capture — tap, hold or drag in for the menu'}
          aria-expanded={view.menu}
          aria-haspopup="menu"
          className="m-cap bg-m-accent text-m-on-accent"
          data-open={view.menu ? 'true' : 'false'}
          data-pressed={view.pressed ? 'true' : 'false'}
          onClick={(e) => {
            // Never while the bar is away (a pushed screen, the keyboard): `inert` stops a real tap,
            // and this stops a programmatic one.
            if (e.detail === 0 && !hidden) gesture.toggleMenu()
          }}
        >
          <PlusGlyph />
        </button>
      </div>
    </div>
  )
}
